import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminHome() {
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  const canQuestions = canManageQuestions(me.data);
  const quizzes = useQuery({ queryKey: ["quizzes-count"], queryFn: () => adminApi.listQuizzes({ pageSize: 1 }) });
  const questions = useQuery({
    queryKey: ["questions-count"],
    queryFn: () => adminApi.listQuestions({ pageSize: 1 }),
    enabled: canQuestions,
  });
  const categories = useQuery({ queryKey: ["categories-count"], queryFn: () => adminApi.listCategories() });

  return (
    <main className="mx-auto max-w-6xl p-6">
      <h1 className="mb-4 text-2xl font-bold text-brand-ink">Dashboard</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Tile to="/admin/quizzes" title="Quizzes" count={quizzes.data?.totalCount} caption="Build, publish, archive." />
        {canQuestions && (
          <>
            <Tile to="/admin/questions" title="Questions" count={questions.data?.totalCount} caption="Add, edit, bulk-import." />
            <Tile to="/admin/categories" title="Categories" count={categories.data?.length} caption="Organize the question bank." />
          </>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {canQuestions && (
          <QuickLink to="/admin/questions/import" title="Bulk import questions (Excel)" />
        )}
        <QuickLink to="/admin/audit-log" title="Audit log" />
      </div>

      <div className="mt-8 card p-6">
        <h2 className="text-lg font-semibold text-brand-ink">Recent quizzes</h2>
        <ul className="mt-3 divide-y divide-brand-ink/10">
          {quizzes.data && quizzes.data.totalCount === 0 && (
            <li className="py-4 text-sm text-brand-ink/60">No quizzes yet — <Link to="/admin/quizzes/new" className="text-brand hover:underline">create one</Link>.</li>
          )}
        </ul>
        <div className="mt-2">
          <Link to="/admin/quizzes" className="text-sm text-brand hover:underline">View all quizzes →</Link>
        </div>
      </div>
    </main>
  );
}

function Tile({ to, title, count, caption }: { to: string; title: string; count?: number; caption: string }) {
  return (
    <Link to={to} className="card block p-6 transition hover:shadow-md">
      <div className="text-xs font-semibold uppercase tracking-wide text-brand-ink/60">{title}</div>
      <div className="mt-2 text-4xl font-bold text-brand">{count ?? "…"}</div>
      <div className="mt-2 text-sm text-brand-ink/70">{caption}</div>
    </Link>
  );
}

function QuickLink({ to, title }: { to: string; title: string }) {
  return (
    <Link to={to} className="card flex items-center justify-between p-4 transition hover:shadow-md">
      <span className="text-brand-ink">{title}</span>
      <span className="text-brand">→</span>
    </Link>
  );
}
